/**
 * Context that lets the chat table renderer create datasets without importing
 * chat models.
 *
 * The table renderer sits in the Streamdown render chain (response-markdown →
 * response-table → chat-dataset-table). Importing chat models/clients there
 * pulls the whole chat graph back into that chain and triggers a circular-import
 * "cannot access X before initialization" crash. So — exactly like the citation
 * feature — the capability travels by context: this leaf only imports React, and
 * the model-backed value is supplied by a provider mounted in the chat page (see
 * `v0/chat/use-chat-table-actions`).
 */

import { createContext, useContext } from 'react';
import type { MarkdownTableData } from '@cotera/client/app/components/ui/data-grid/markdown-table';

export type CreatedDataset = { id: string; name: string };

export type ChatTableActions = {
  /**
   * Materialize the table into a dataset artifact; resolves null on failure.
   * `messageId` is the message the table came from — when present, the dataset
   * is persisted as a tool-call result on that message.
   */
  createDataset: (
    data: MarkdownTableData,
    messageId: string | null
  ) => Promise<CreatedDataset | null>;
  /** Focus an already-created dataset in the artifact panel. */
  openDataset: (id: string) => void;
};

const ChatTableActionsContext = createContext<ChatTableActions | null>(null);

export const ChatTableActionsProvider = ChatTableActionsContext.Provider;

/** Null outside a chat that provides the actions (plain markdown, tests, …). */
export const useChatTableActions = (): ChatTableActions | null =>
  useContext(ChatTableActionsContext);

// createManual's schema caps: ≤200 columns, ≤1000 rows.
const MAX_COLUMNS = 200;
const MAX_ROWS = 1000;

/**
 * Turn the display table into `createManual` input: unique non-empty column
 * names, and rows keyed by those names (the API rejects unknown/positional keys).
 */
export const buildDatasetInput = (
  data: MarkdownTableData
): {
  columns: { name: string; type: 'string' }[];
  rows: Record<string, string>[];
} => {
  const used = new Map<string, number>();
  const columnNames = data.headers
    .slice(0, MAX_COLUMNS)
    .map((header, index) => {
      const base =
        header.text.trim() === '' ? `Column ${index + 1}` : header.text.trim();
      const seen = used.get(base) ?? 0;
      used.set(base, seen + 1);
      return seen === 0 ? base : `${base} (${seen + 1})`;
    });

  const columns = columnNames.map((name) => ({
    name,
    type: 'string' as const,
  }));
  const rows = data.rows
    .slice(0, MAX_ROWS)
    .map((cells) =>
      Object.fromEntries(
        columnNames.map((name, index) => [name, cells[index]?.text ?? ''])
      )
    );
  return { columns, rows };
};
